import { jsxs, jsx } from 'react/jsx-runtime';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useMutationCheckListItemState } from '../../onboarding/mutations/useMutationCheckListItemState.js';
import '../../onboarding/onboarding.config.js';
import { resourceMutationKeys } from './resourceMutationKeys.js';
import { resourceQueryPredicates } from '../queries/resourceQueryPredicates.js';
import { createOptimisticResource } from '../utils/createOptimisticResource.js';
import { useQueryCacheResourceHelpers } from '../utils/useQueryCacheResourceHelpers.js';
import { captureException } from '@sentry/core';
import { useQueryClient } from '@tanstack/react-query';
import { useWoody } from '../../woody/woody.js';
import { toast } from '../../store/alerts.js';
import { v4 } from 'uuid';
import { getCompatLanguage } from '@fabric-app/compat';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { getErrorDetail } from '../../errors/errors.utils.js';
import { useMutationWithOptimisticContext } from '@fabric-app/queries/useMutationWithOptimisticContext';
import { ErrorType } from '../../errors/errors.types.js';
import { useResourceClickHandler } from '../components/ResourcePreview/hooks/useResourceClickHandler.js';
import { ErrorMessages } from '../../errors/errors.messages.js';
import { isTruthy } from '@fabric-app/utils/guards';
import { useQueryDirectSubfolderCountPerResourceIdInvalidate } from '../queries/useQueryDirectChildCountPerResourceId.js';
import { dispatchFabricEvent } from '../../hooks/useFabricEvent.js';
import { createLogger } from '@fabric-app/utils/logging';
import { OnboardingCheckListActionId } from '@fabric-app/types/onboarding';

const resourceTypeToChecklistActionId = (type) => {
  switch (type) {
    case "image":
    case "audio":
    case "document":
    case "video":
    case "default":
      return OnboardingCheckListActionId.UPLOAD_FIRST_FILE;
    case "notepad":
      return OnboardingCheckListActionId.CREATE_FIRST_NOTEPAD;
    case "bookmark":
      return OnboardingCheckListActionId.SAVE_FIRST_LINK;
    case "folder":
    case "highlight":
      return null;
    default: {
      console.error("Unknown resource type to onboarding checklist", type);
      return null;
    }
  }
};
const getFailureMessageByCreateType = (createType) => {
  switch (createType) {
    case "note":
      return "Failed to create note";
    case "file":
      return "Failed to upload file";
    case "bookmark":
      return "Failed to save bookmark";
    case "folder":
      return "Failed to create folder";
    default:
      return "Failed to create";
  }
};
const mapOptmisticPrivateTagsToAssignableTags = (tags) => {
  const uniqueTagsByName = /* @__PURE__ */ new Map();
  tags?.forEach((tag) => {
    const foundTag = uniqueTagsByName.get(tag.name);
    if (foundTag) {
      if (foundTag.isDraft && !tag.isDraft) {
        uniqueTagsByName.set(tag.name, tag);
      }
    } else {
      uniqueTagsByName.set(tag.name, tag);
    }
  });
  const uniqueTagsById = /* @__PURE__ */ new Map();
  uniqueTagsByName.forEach((tag) => {
    uniqueTagsById.set(tag.id, tag);
  });
  return Array.from(uniqueTagsById.values()).map((tag) => tag.isDraft ? {
    name: tag.name
  } : {
    id: tag.id
  });
};
const createDataToPathBody = ({ createType, body: bodyArg }) => {
  const body = "tags" in bodyArg ? {
    ...bodyArg,
    tags: mapOptmisticPrivateTagsToAssignableTags(bodyArg.tags)
  } : bodyArg;
  switch (createType) {
    case "note":
      return {
        path: "/v2/notepads",
        body
      };
    case "voiceNote":
      return {
        path: "/v2/voicenotes",
        body
      };
    case "file": {
      const nextBody = {
        ...body
      };
      if ("name" in nextBody) delete nextBody.name;
      return {
        path: "/v2/files",
        body: nextBody
      };
    }
    case "bookmark":
      return {
        path: "/v2/bookmarks",
        body
      };
    case "folder": {
      console.warn("Creating folder might break, waiting for endpoint update");
      return {
        path: "/v2/folders",
        body
      };
    }
    case "highlight": {
      return {
        path: "/v2/highlights",
        body
      };
    }
    default: {
      throw new Error(`Unknown createType ${createType}`);
    }
  }
};
const kindToToastTitle = (kind) => {
  switch (kind) {
    case "bookmark":
      return "Link";
    case "document":
      return "Document";
    case "image":
      return "Image";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    case "notepad":
      return "Note";
    case "folder":
      return "Folder";
    case "default":
      return "Item";
    default:
      return kind;
  }
};
const useMutationCreateResourceV2 = () => {
  const { client } = useWoody();
  const { track } = useAnalytics();
  const { data: user } = useQueryUserInfo();
  const logger = createLogger("useMutationCreateResourceV2");
  const { mutate: mutateCheckListItemState } = useMutationCheckListItemState();
  const { addNewResourcesToCache } = useQueryCacheResourceHelpers();
  const queryClient = useQueryClient();
  const resourceClickHandler = useResourceClickHandler();
  const LANGUAGE = getCompatLanguage();
  const invalidateQueryDirectSubfolderCountPerResourceId = useQueryDirectSubfolderCountPerResourceIdInvalidate();
  const mutation = useMutationWithOptimisticContext({
    // we keep the _variables to let TS infer the types
    getOptimisticContext: (_variables) => {
      return {
        optimisticId: v4()
      };
    },
    mutationKey: resourceMutationKeys.createResource,
    mutationFn: async ({ data, context }) => {
      const { path, body } = createDataToPathBody(data);
      logger.info("Creating resource", {
        path,
        body
      });
      const resource = await client.v2(path, {
        method: "post",
        body
      });
      if (!resource) {
        throw new Error("Failed to create resource. This shouldn't happen but v2/create returned nothing.");
      }
      logger.info("Resource created", {
        resource
      });
      return {
        ...resource,
        draftId: context?.optimisticId
      };
    },
    onMutate: ({ data, context, disableAddToCache }) => {
      if (!user || disableAddToCache) return;
      const optimisticResource = createOptimisticResource(data, user, {
        id: context?.optimisticId
      });
      return {
        optimisticResource,
        addResourceCache: addNewResourcesToCache([
          optimisticResource
        ])
      };
    },
    onSuccess: (newResource, { disableToast, action = "new-modal", disableAddToCache, data: { createType } }, context) => {
      const parentId = newResource.parent?.id;
      if (parentId && !disableAddToCache && context) {
        const { optimisticResource } = context;
        queryClient.setQueriesData({
          predicate: (q) => resourceQueryPredicates.resourceListAny(q)
        }, (prevData) => {
          if (!prevData) {
            return prevData;
          }
          const next = {
            ...prevData,
            pages: prevData.pages.map((page) => {
              return {
                ...page,
                resources: (page?.resources || []).map((resource) => {
                  if ("draftId" in resource && resource.draftId === optimisticResource.draftId) {
                    let nextData = newResource.data;
                    if (optimisticResource.kind === "bookmark" && newResource.kind === "bookmark" && newResource.data && "webpage" in newResource.data && optimisticResource.data && "webpage" in optimisticResource.data) {
                      nextData = {
                        ...resource.data,
                        webpage: {
                          ...newResource.data.webpage,
                          image: {
                            ...optimisticResource.data.webpage?.image,
                            ...newResource.data.webpage?.image,
                            url: optimisticResource.data.webpage?.image?.url || newResource.data.webpage?.image?.url
                          }
                        }
                      };
                    }
                    const updatedCacheItem = {
                      ...newResource,
                      draftId: resource.draftId,
                      /**
                      * we want to keep thumbnail of the optimistic resource if available
                      * to prevent flashing and re-loading of the thumbnail
                      */
                      thumbnail: optimisticResource.thumbnail || newResource.thumbnail,
                      data: nextData
                    };
                    delete updatedCacheItem.isDraft;
                    return updatedCacheItem;
                  }
                  return resource;
                })
              };
            })
          };
          return next;
        });
      }
      const actionId = resourceTypeToChecklistActionId(newResource.kind);
      if (actionId) {
        mutateCheckListItemState({
          actionId,
          state: true
        });
      }
      if (!disableToast) {
        toast({
          id: "create-new-resource",
          replace: true,
          content: /* @__PURE__ */ jsxs("span", {
            children: [
              /* @__PURE__ */ jsx("span", {
                style: {
                  textTransform: "capitalize"
                },
                children: kindToToastTitle(newResource.kind)
              }),
              ` ${LANGUAGE.created}`
            ]
          }),
          onClick: () => {
            resourceClickHandler(newResource);
          }
        });
      }
      track(AnalyticsEvents.CreatedFdoc, {
        type: newResource.kind,
        createType,
        action
      });
      dispatchFabricEvent(newResource.kind === "folder" ? "fabric-folder-created" : "fabric-resource-created", {});
    },
    onError(error, variables, context) {
      if (context) {
        context.addResourceCache.resetCacheToPreOptimisticState();
      }
      if (getErrorDetail(error) === ErrorType.EXCEEDS_ITEM_LIMIT) {
        logger.info("Exceeded item limit", {
          error
        });
        toast({
          content: ErrorMessages[ErrorType.EXCEEDS_ITEM_LIMIT],
          id: ErrorType.EXCEEDS_ITEM_LIMIT
        });
        return;
      }
      logger.info("Failed to create resource", {
        error,
        variables,
        context
      });
      captureException(new Error([
        "Failed to create resource",
        error.message
      ].filter(isTruthy).join(": ")), {
        extra: {
          error,
          ...variables
        }
      });
      toast({
        id: "create-new-resource-failure",
        replace: true,
        content: getFailureMessageByCreateType(variables.data.createType)
      });
    },
    onSettled: (resource, _e, _rs, context) => {
      context?.addResourceCache.clearPendingState();
      if (resource && resource?.kind === "folder" && resource?.parent?.id) {
        invalidateQueryDirectSubfolderCountPerResourceId([
          resource?.parent?.id
        ]);
      }
    }
  });
  return mutation;
};

export { useMutationCreateResourceV2 };
//# sourceMappingURL=useMutationCreateResourceV2.js.map
